import { installDrainWriteHooks } from './drain/drain-write-tracker';
import Dexie, { Table } from 'dexie';

/**
 * Client id + originator stamps on queued creates (P2-05; see client-id.ts).
 * Non-indexed properties: adding them needs no Dexie version bump. Absent on
 * items queued by older builds.
 */
export interface OriginatorStamp {
  /** UUID sent as the server row's `id`, so a replay cannot create a duplicate. */
  client_id?: string;
  origin_user_id?: string | null;
  origin_tenant_id?: string | null;
  origin_device_session_id?: string | null;
  /** Device time the item was created. */
  occurred_at?: string;
}

export interface PendingMutation extends OriginatorStamp {
  id?: string;
  endpoint: string;
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body: Record<string, unknown>;
  attachments?: Array<{ name: string; blob: Blob }>;
  createdAt: number;
  retryCount: number;
  status: 'pending' | 'syncing' | 'failed';
  errorMessage?: string;
  // Tenant scoping: captured at enqueue time so replay never crosses tenants
  // (prevents a mutation queued under Tenant A from POSTing to Tenant B after a switch).
  tenant_id?: string | null;
}

export interface CachedTask extends OriginatorStamp {
  id: string;
  offline_id?: string;
  tenant_id: string;
  incident_id: string | null;
  title: string;
  description?: string;
  status: string;
  priority: string;
  assigned_to?: string;
  assigned_esf_id?: string;
  /**
   * Unified org tree assignment (incident_org_nodes, Phase 3).
   * Plain (non-indexed) property — Dexie only declares indexes in stores(),
   * so adding this field requires no schema version bump.
   */
  org_node_id?: string | null;
  task_type_id?: string;
  location?: { lat: number; lon: number };
  location_name?: string;
  location_address?: string;
  due_at?: string;
  custom_fields: Record<string, unknown>;
  requestor_name?: string;
  requestor_phone?: string;
  requestor_email?: string;
  tags?: string[];
  parent_task_id?: string;
  source_module?: string;
  source_id?: string;
  created_at: string;
  updated_at: string;
  // Sync tracking
  is_synced: number; // 0 = unsynced, 1 = synced (Dexie indexes booleans as 0/1)
  synced_at?: string;
  pending_changes?: boolean;
  /** Deletion tombstone: the row was deleted offline and the server DELETE is
   *  still pending. Non-indexed; sync sends a DELETE then removes the row. */
  pending_delete?: boolean;
  /** Status changed offline and not yet pushed. PATCH /tasks/:id does NOT accept
   *  `status` (updateTaskSchema omits it) — it needs PATCH /tasks/:id/status —
   *  so sync must replay it separately or a field completion is lost. Only set
   *  when the change could not be pushed immediately. Non-indexed. */
  pending_status?: string;
  pending_status_notes?: string;
  /** Assignment changed offline and not yet pushed. `assigned_to` likewise is
   *  not accepted by PATCH /tasks/:id — it needs POST /tasks/:id/assign.
   *  Non-indexed. */
  pending_assignment?: boolean;
  last_error?: string;
}

export interface CachedShelter {
  id: string;
  tenant_id: string;
  incident_id: string;
  name: string;
  address: string;
  location: { lat: number; lon: number };
  capacity: number;
  current_occupancy: number;
  status: string;
}

export interface CachedIncident {
  id: string;
  offline_id?: string;
  tenant_id: string;
  incident_number: string;
  name: string;
  short_name?: string;
  type: string;
  status: string;
  classification: string;
  complexity_level: string;
  description?: string;
  objectives?: string;
  center_lat?: number;
  center_lng?: number;
  jurisdiction_ids: string[];
  parent_incident_id?: string;
  start_time: string;
  end_time?: string;
  operational_period_duration_minutes?: number;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  // Sync tracking
  is_synced: boolean;
  synced_at?: string;
  pending_changes?: boolean;
  last_error?: string;
  // Conflict resolution
  conflict_state?: 'none' | 'detected' | 'resolved';
  server_updated_at?: string;
  server_snapshot?: Record<string, unknown>;
}

export interface CachedBoardPost extends OriginatorStamp {
  id: string;
  offline_id?: string; // Local UUID for offline-created posts
  tenant_id: string;
  incident_id: string | null;
  title: string;
  content: string;
  summary?: string;
  classification: string;
  priority: string;
  target_audience?: string[];
  target_units?: string[];
  effective_at?: string;
  expires_at?: string;
  is_pinned: boolean;
  pinned_until?: string;
  author_id?: string;
  author_name?: string;
  author_unit?: string;
  is_published: boolean;
  tags: string[];
  acknowledgement_count: number;
  comment_count: number;
  created_at: string;
  updated_at: string;
  // Sync tracking
  is_synced: boolean;
  synced_at?: string;
  pending_changes?: boolean;
  last_error?: string;
}

// Damage Assessment offline storage
export interface CachedDamageAssessment extends OriginatorStamp {
  id: string; // Server ID or offline_id
  offline_id?: string; // Local UUID for offline-created assessments
  tenant_id: string;
  incident_id: string | null;
  zone_id?: string;
  assessment_number?: string;
  address: string;
  // Where the address came from (migration 680): 'entered' = a person typed it, so the
  // server never overwrites it. Absent with a blank address = the server fills it after
  // sync. Non-indexed optional field (not in .stores()), so no Dexie version bump.
  address_source?: 'entered' | 'reverse_geocode';
  city?: string;
  state?: string;
  zip_code?: string;
  county?: string;
  location: { lat: number; lng: number } | null;
  affected_area?: unknown; // GeoJSON Polygon
  property_type: string;
  structure_type?: string; // Server-side field name for property_type
  ownership_type?: string;
  overall_severity: string;
  status: string;
  damage_categories: Record<string, { severity: string; notes: string }>;
  occupant_name?: string;
  occupant_phone?: string;
  occupant_email?: string;
  is_displaced: boolean;
  is_habitable?: boolean; // Server field — independent from is_displaced
  occupants_displaced?: number;
  estimated_damage_cost?: number;
  insurance_coverage?: boolean;
  fema_assistance_eligible?: boolean;
  // Non-indexed optional field — carried so an offline-authored FEMA PA category
  // survives to the sync POST. Not listed in .stores(), so no version bump.
  pa_category?: string;
  damage_description?: string;
  notes?: string;
  assigned_to?: string;
  assessed_by?: string;
  assessed_at?: string;
  metadata?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  client_created_at?: string;
  // Regrid parcel data
  regrid_owner?: string;
  regrid_assessed_value?: number;
  regrid_market_value?: number;
  regrid_year_built?: number;
  regrid_lot_size_sqft?: number;
  regrid_building_sqft?: number;
  regrid_land_use?: string;
  regrid_zoning?: string;
  regrid_parcel_id?: string;
  // Sync tracking
  is_synced: boolean;
  synced_at?: string;
  pending_changes?: boolean;
  last_error?: string;
  // Photo sync tracking (additive — separate from assessment data sync)
  photos_synced?: boolean;
  unsynced_photo_count?: number;
  // Conflict resolution
  server_updated_at?: string;
  conflict_state?: 'none' | 'detected' | 'resolved';
  server_snapshot?: Record<string, unknown>;
}

// (offlineMapRegions / offlineMapTiles -- the offline basemap tile scraper's
// tables -- were dropped in version 32; see the note there.)

// PDA Zone offline storage
export interface CachedPdaZoneSet {
  id: string;
  tenant_id: string;
  incident_id?: string;
  name: string;
  grid_cell_size_meters: number;
  boundary?: unknown;
  zone_count?: number;
  zones_complete?: number;
  created_at: string;
  updated_at: string;
}

export interface CachedPdaZone {
  id: string;
  tenant_id: string;
  zone_set_id: string;
  zone_number: number;
  name?: string;
  boundary: unknown;
  status: string;
  assigned_to?: string;
  assigned_to_name?: string;
  coverage_percent: number;
  grid_cells_total: number;
  grid_cells_visited: number;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface OfflineBreadcrumb {
  id: string;
  zone_id: string;
  lat: number;
  lng: number;
  accuracy?: number;
  altitude?: number;
  speed?: number;
  heading?: number;
  recorded_at: string;
  is_synced: boolean;
}

// Offline photo storage with blob
export interface OfflinePhoto extends OriginatorStamp {
  id: string; // Local UUID
  assessment_id: string; // Links to CachedDamageAssessment.id or offline_id
  blob: Blob;
  filename: string;
  content_type: string;
  category?: string; // damage category this photo relates to
  caption?: string;
  exif_data?: {
    lat?: number;
    lng?: number;
    timestamp?: string;
  };
  created_at: number;
  // Sync tracking
  is_synced: boolean;
  server_url?: string; // URL after upload
  upload_error?: string;
}

// COOP offline storage
export interface CachedCoopPlan {
  id: string;
  tenant_id: string;
  name: string;
  status: string;
  version: number;
  description?: string;
  essential_function_count: number;
  updated_at: string;
  is_synced: boolean;
  synced_at?: string;
}

export interface CachedEssentialFunction {
  id: string;
  plan_id: string;
  name: string;
  priority_tier: string;
  rto_hours: number;
  rpo_hours: number;
  status: string;
  is_synced: boolean;
}

export interface CachedSuccessionOrder {
  id: string;
  plan_id: string;
  position_title: string;
  successors: unknown[];
  geographic_diversity_score?: number;
  is_synced: boolean;
}

export interface CachedCoopFacility {
  id: string;
  plan_id: string;
  name: string;
  facility_type: string;
  readiness_status: string;
  address?: string;
  location?: { lat: number; lng: number };
  is_synced: boolean;
}

export interface OfflineActivationLog {
  id: string;
  activation_id: string;
  log_type: string;
  message: string;
  created_at: string;
  is_synced: boolean;
}

// ICS 214 Unit Log offline storage
export interface CachedUnitLog {
  id: string;
  offline_id?: string;
  tenant_id: string;
  incident_id: string | null;
  operational_period_id: string | null;
  is_non_incident: boolean;
  unit_name: string;
  ics_position: string | null;
  home_agency: string | null;
  supervisor_id: string | null;
  supervisor_name: string | null;
  status: string;
  finalized_at: string | null;
  finalized_by: string | null;
  handoff_summary: string | null;
  entries_count?: number;
  roster_count?: number;
  next_actions_count?: number;
  created_at: string;
  updated_at: string;
  // Sync tracking
  is_synced: boolean;
  synced_at?: string;
  pending_changes?: boolean;
  last_error?: string;
  // Conflict resolution
  conflict_state?: 'none' | 'detected' | 'resolved';
  server_updated_at?: string;
  server_snapshot?: Record<string, unknown>;
}

export interface CachedActivityEntry {
  id: string;
  offline_id?: string;
  unit_log_id: string;
  tenant_id: string;
  entry_time: string;
  activity_narrative: string;
  category_tags: string[];
  linked_task_id?: string | null;
  created_by_name?: string;
  created_at: string;
  updated_at: string;
  // Sync tracking
  is_synced: boolean;
  synced_at?: string;
  pending_changes?: boolean;
  last_error?: string;
}

// Supply Request offline storage
export interface CachedSupplyRequest extends OriginatorStamp {
  id: string;
  incident_id: string;
  data: any; // full supply request object
  status: string;
  priority: string;
  is_synced: boolean;
  synced_at?: string;
  updated_at: string;
}

// Infrastructure offline storage
export interface CachedCriticalInfrastructure {
  id: string;
  offline_id?: string;
  tenant_id: string;
  incident_id?: string;
  name: string;
  category: string;
  status: string;
  data: Record<string, unknown>;
  is_synced: boolean;
  synced_at?: string;
  pending_changes?: boolean;
  updated_at: string;
  last_error?: string;
}

export interface CachedRoadClosure {
  id: string;
  offline_id?: string;
  tenant_id: string;
  incident_id?: string;
  name: string;
  status: string;
  data: Record<string, unknown>;
  is_synced: boolean;
  synced_at?: string;
  pending_changes?: boolean;
  updated_at: string;
  last_error?: string;
}

export interface CachedUtilityOutage {
  id: string;
  offline_id?: string;
  tenant_id: string;
  incident_id?: string;
  name: string;
  utility_type: string;
  status: string;
  data: Record<string, unknown>;
  is_synced: boolean;
  synced_at?: string;
  pending_changes?: boolean;
  updated_at: string;
  last_error?: string;
}

// SUAS offline snapshot storage
export interface OfflineSnapshot {
  id: string; // Auto-generated UUID
  mission_id: string;
  blob: Blob; // JPEG data
  lat: number;
  lng: number;
  altitude_meters?: number;
  heading?: number;
  captured_at: string;
  is_synced: boolean;
  synced_at?: string;
  sync_error?: string;
}

export interface PersonnelPositionBuffer {
  /** Primary key. Rows written since P0-15 use the point's own point_id. */
  id: string;
  /**
   * Server idempotency key (UNIQUE(session_id, point_id)). Absent on rows
   * buffered by older builds; the sync uses `id` for those, which is equally
   * stable. Not indexed, so adding it needs no Dexie version bump.
   */
  point_id?: string;
  session_id: string;
  lat: number;
  lng: number;
  altitude?: number;
  accuracy?: number;
  speed?: number;
  heading?: number;
  battery_percent?: number;
  recorded_at: string;
  is_synced: boolean;
  created_at: number;
  /**
   * Set when the server refused the point with a permanent 4xx (validation,
   * session gone). syncAll() never resends it. It stays unsynced, so the
   * pending count and the Discard dialog still cover it.
   */
  dead_letter?: boolean;
  dead_letter_status?: number;
  dead_lettered_at?: number;
}

// Offline supply request creation (field operator offline mode)
export interface OfflineSupplyRequest extends OriginatorStamp {
  localId?: number; // Auto-incremented local ID
  id?: string; // Server UUID after sync
  incident_id: string | null;
  tenant_id: string;
  title: string;
  description?: string;
  priority: 'low' | 'medium' | 'high' | 'urgent' | 'critical';
  items: Array<{
    name: string;
    quantity: number;
    unit: string;
    notes?: string;
  }>;
  location?: string;
  needed_by?: string;
  requesting_unit?: string;
  delivery_address?: string;
  delivery_coordinates?: { lat: number; lng: number };
  delivery_instructions?: string;
  is_synced: number; // 0 = unsynced, 1 = synced (Dexie indexes booleans as 0/1)
  status: string;
  created_at: string;
  updated_at: string;
}

// Cached resource types for offline supply request dropdowns
export interface CachedResourceType {
  id: string;
  category_id: string;
  code: string;
  name: string;
  description?: string;
  unit_of_measure?: string;
  category_name?: string;
  category_code?: string;
}

// Cached credentials for offline lookup
export interface CachedCredential {
  id: string;
  tenant_id: string;
  credential_number: string;
  entity_type: string;
  status: string;
  display_name: string;
  photo_url?: string;
  expires_at?: string;
  // Person data (flattened for offline)
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  organization?: string;
  title?: string;
  department?: string;
  // Qualification codes (comma-separated for offline)
  qualification_codes?: string;
  // Full credential JSON for detail view
  data: Record<string, unknown>;
  // Sync tracking
  is_synced: boolean;
  last_synced_at?: string;
}

export interface CachedQualificationType {
  id: string;
  code: string;
  name: string;
  category: string;
  description?: string;
  level?: string;
  nims_typed: boolean;
  sort_order: number;
  is_active: boolean;
}

// Cached contacts for offline phone/email lookup
export interface CachedContact {
  id: string;
  tenant_id: string;
  name: string;
  contact_type: 'person' | 'organization' | 'role';
  phone?: string;
  email?: string;
  organization?: string;
  department?: string;
  ics_position?: string;
  is_active: boolean;
  photo_url?: string;
  // Handling caveat + reachability, cached verbatim from the server row.
  // These MUST be cached rather than re-derived: the offline contact list used
  // to hard-code clearance_level:'internal'/preferred_channel:'email' for every
  // row, so a `law_enforcement` or `health` contact displayed a calm "Internal"
  // chip to a disconnected operator, who could then disclose restricted detail
  // on the strength of that badge. Optional because rows written by an older
  // build have neither field — callers must treat `undefined` as UNKNOWN, never
  // as a default. No Dexie version bump is needed: neither field is indexed.
  clearance_level?: string;
  preferred_channel?: string;
  updated_at: string;
  // Sync tracking
  is_synced: boolean;
  synced_at?: string;
}

// FEMA cost entry offline storage
export interface CachedFemaCost {
  id: string;
  offline_id: string;
  tenant_id: string;
  incident_id: string;
  cost_type: 'personnel' | 'equipment' | 'supplies' | 'contracts';
  pa_category?: string;
  amount?: number;
  description?: string;
  worked_date?: string;
  used_date?: string;
  hours?: number;
  rate_used?: number;
  total_cost?: number;
  personnel_name?: string;
  equipment_name?: string;
  vendor_name?: string;
  is_synced: boolean;
  synced_at?: string;
  pending_changes: boolean;
  /** Deletion tombstone: deleted offline, server DELETE still pending. */
  pending_delete?: boolean;
  last_error?: string;
  conflict_state?: string;
  server_updated_at?: string;
  server_snapshot?: any;
  created_at: string;
  updated_at: string;
  details?: Record<string, any>;
}

// Cached photo blobs for offline rendering (contacts, credentials, members)
export interface CachedPhoto {
  id: string; // "{entity_type}:{entity_id}" e.g. "contact:uuid"
  entity_type: 'contact' | 'credential' | 'member';
  entity_id: string;
  blob: Blob;
  content_type: string;
  size_bytes: number;
  cached_at: string;
}

// Cached orthophoto regions for offline use
export interface CachedOrthophotoRegion {
  id: string;
  task_id: string;
  name: string;
  bounds: {
    north: number;
    south: number;
    east: number;
    west: number;
  };
  zoom_range: { min: number; max: number };
  tile_count: number;
  total_size: number;
  created_at: string;
}

// Individual cached orthophoto tile
export interface CachedOrthophotoTile {
  id?: number; // auto-incremented
  region_id: string;
  task_id: string;
  z: number;
  x: number;
  y: number;
  data: Blob;
}

// Cached team members for offline lookup
export interface CachedMember {
  id: string; // user_id
  tenant_id: string;
  email: string;
  name: string;
  role_name?: string;
  status: string;
  avatar_url?: string;
  phone?: string;
  is_synced: boolean;
  synced_at?: string;
}

// Cached map features for offline use
export interface CachedMapFeature {
  id: string; // "{layer}:{featureId}"
  tenant_id: string;
  layer: string;
  feature_id: string;
  geojson: Record<string, unknown>; // Full GeoJSON Feature
  is_synced: boolean;
  cached_at: number;
}

// ICS 214 Roster pending mutation for offline support
export interface ICS214RosterPendingMutation {
  id?: number; // auto-incremented
  unit_log_id: string;
  action: 'add' | 'check_in' | 'check_out' | 'update' | 'delete';
  payload: Record<string, unknown>;
  created_at: string;
  synced_at?: string;
}

// Cached roster entries for offline viewing
export interface CachedRosterEntry {
  id: string;
  unit_log_id: string;
  person_name: string;
  ics_position?: string | null;
  home_agency?: string | null;
  contact_info?: string | null;
  contact_id?: string | null;
  user_id?: string | null;
  agency_id?: string | null;
  check_in_time?: string | null;
  check_out_time?: string | null;
  qualifications: string[];
  total_hours?: number | null;
  regular_hours?: number | null;
  overtime_hours?: number | null;
  rate_base?: number | null;
  rate_overtime?: number | null;
  rate_holiday?: number | null;
  rate_source?: string | null;
  calculated_cost?: number | null;
  created_at: string;
  updated_at: string;
  is_synced: boolean;
  synced_at?: string;
}

// ICS Form draft auto-save (local only, not synced to server)
export interface FormDraft {
  id?: number;
  form_id: string | null;
  template_id: string;
  incident_id: string | null;
  form_data: Record<string, unknown>;
  updated_at: string;
  user_id: string;
  is_synced: number;
}

// Offline ICS form storage
export interface OfflineForm extends OriginatorStamp {
  localId?: number;
  id?: string;
  offline_id: string;
  template_id: string;
  template_name: string;
  incident_id: string | null;
  tenant_id: string;
  title: string;
  form_data: Record<string, unknown>;
  status: 'draft' | 'pending_signature' | 'approved';
  is_synced: number;
  created_at: string;
  updated_at: string;
  synced_at?: string;
  last_error?: string;
}

// Cached form template for offline template selection
export interface CachedFormTemplate {
  id: string;
  tenant_id: string | null;
  code: string;
  name: string;
  description: string | null;
  category: string | null;
  field_schema: Record<string, unknown>;
  is_active: boolean;
  sort_order: number;
  cached_at: string;
}

// Offline shelter check-in record
export interface OfflineCheckin extends OriginatorStamp {
  localId?: number;
  id?: string;
  shelter_id: string;
  tenant_id: string;
  resident_name: string;
  /** Name parts exactly as the responder typed them. ABSENT on legacy records,
   *  which stored only `resident_name` — syncCheckin falls back to splitting it. */
  first_name?: string;
  last_name?: string;
  resident_count: number;
  phone?: string;
  /** Life-safety fields the field form captures. They live here because anything
   *  the form collects but this record cannot hold is DISCARDED at save time and
   *  then the row is marked is_synced — a resident's medical needs and
   *  next-of-kin would be gone with no warning, and the shelter roster the
   *  medical branch triages would show them as having no medical flags.
   *  Non-indexed, so adding them needs no Dexie version bump. */
  date_of_birth?: string;
  gender?: string;
  has_medical_needs?: boolean;
  medical_notes?: string;
  emergency_contact_name?: string;
  emergency_contact_phone?: string;
  /** Array of specialNeedsTypeEnum members. `string` is the LEGACY comma-joined
   *  form still present in records queued by older builds — normalizeSpecialNeeds()
   *  in shelter-checkin.service.ts accepts both. Non-indexed, so widening the
   *  type needs no Dexie version bump. */
  special_needs?: string | string[];
  notes?: string;
  pets: string[];
  is_synced: number;
  created_at: string;
  updated_at: string;
}

/**
 * Kinds the safety outbox holds (lane-2 field events the device owns until
 * delivered). Only what the bridge actually records: it sends no PAR or
 * emergency-traffic acknowledgements, so the handoff never claims any.
 */
export type SafetyOutboxKind = 'sos' | 'checkin';

/**
 * Safety outbox entry (P1-12, kept durable for the drain, migration plan §6.2).
 * Keyed by the field event id, reused on every re-send. `status` is a string,
 * never a boolean is_synced (IndexedDB cannot index booleans).
 */
export interface SafetyOutboxEntry extends OriginatorStamp {
  event_id: string;
  kind: SafetyOutboxKind;
  /** The field event exactly as sent. */
  event: Record<string, unknown>;
  status: 'pending' | 'delivered' | 'rejected';
  created_at: number;
  delivered_at?: number;
  /**
   * Which route stored it: field_v1 (journalled in field_events, so the server
   * recognises a re-send) or legacy (POST /personnel-tracking/sos, NOT
   * journalled: it must never be handed off to be sent again).
   */
  delivered_via?: 'field_v1' | 'legacy';
  server_event_id?: string;
  safety_event_id?: string;
  /** When a human (the console) acknowledged it: it then stops being an open critical event. */
  human_acknowledged_at?: number;
  attempts: number;
  last_error?: string;
}

export class OfflineDatabase extends Dexie {
  pendingMutations!: Table<PendingMutation, string>;
  cachedTasks!: Table<CachedTask, string>;
  cachedShelters!: Table<CachedShelter, string>;
  cachedIncidents!: Table<CachedIncident, string>;
  cachedBoardPosts!: Table<CachedBoardPost, string>;
  cachedDamageAssessments!: Table<CachedDamageAssessment, string>;
  offlinePhotos!: Table<OfflinePhoto, string>;
  cachedPdaZoneSets!: Table<CachedPdaZoneSet, string>;
  cachedPdaZones!: Table<CachedPdaZone, string>;
  offlineBreadcrumbs!: Table<OfflineBreadcrumb, string>;
  cachedCoopPlans!: Table<CachedCoopPlan, string>;
  cachedEssentialFunctions!: Table<CachedEssentialFunction, string>;
  cachedSuccessionOrders!: Table<CachedSuccessionOrder, string>;
  cachedCoopFacilities!: Table<CachedCoopFacility, string>;
  offlineActivationLogs!: Table<OfflineActivationLog, string>;
  offlineSnapshots!: Table<OfflineSnapshot, string>;
  cachedUnitLogs!: Table<CachedUnitLog, string>;
  cachedActivityEntries!: Table<CachedActivityEntry, string>;
  cachedSupplyRequests!: Table<CachedSupplyRequest, string>;
  personnelPositionBuffer!: Table<PersonnelPositionBuffer, string>;
  cachedCriticalInfrastructure!: Table<CachedCriticalInfrastructure, string>;
  cachedRoadClosures!: Table<CachedRoadClosure, string>;
  cachedUtilityOutages!: Table<CachedUtilityOutage, string>;
  cachedMapFeatures!: Table<CachedMapFeature, string>;
  cachedContacts!: Table<CachedContact, string>;
  cachedCredentials!: Table<CachedCredential, string>;
  cachedQualificationTypes!: Table<CachedQualificationType, string>;
  cachedFemaCosts!: Table<CachedFemaCost, string>;
  cachedPhotos!: Table<CachedPhoto, string>;
  cachedMembers!: Table<CachedMember, string>;
  cachedOrthophotoRegions!: Table<CachedOrthophotoRegion, string>;
  cachedOrthophotoTiles!: Table<CachedOrthophotoTile, number>;
  ics214RosterPending!: Table<ICS214RosterPendingMutation, number>;
  cachedRosterEntries!: Table<CachedRosterEntry, string>;
  formDrafts!: Table<FormDraft, number>;
  offlineForms!: Table<OfflineForm, number>;
  cachedFormTemplates!: Table<CachedFormTemplate, string>;
  offlineCheckins!: Table<OfflineCheckin, number>;
  offlineSupplyRequests!: Table<OfflineSupplyRequest, number>;
  cachedResourceTypes!: Table<CachedResourceType, string>;
  safetyOutbox!: Table<SafetyOutboxEntry, string>;

  constructor() {
    super('EmergencyOpsDB');

    // Version 1: Original tables
    this.version(1).stores({
      pendingMutations: '++id, createdAt, status',
      cachedTasks: 'id, tenant_id, incident_id, assigned_to, status',
      cachedShelters: 'id, tenant_id, incident_id, status',
      cachedBoardPosts: 'id, tenant_id, incident_id',
      userContext: 'user_id',
    });

    // Version 2: Add damage assessment offline support
    this.version(2).stores({
      pendingMutations: '++id, createdAt, status',
      cachedTasks: 'id, tenant_id, incident_id, assigned_to, status',
      cachedShelters: 'id, tenant_id, incident_id, status',
      cachedBoardPosts: 'id, tenant_id, incident_id',
      userContext: 'user_id',
      cachedDamageAssessments: 'id, offline_id, tenant_id, incident_id, status, is_synced, updated_at',
      offlinePhotos: 'id, assessment_id, is_synced, created_at',
    });

    // Version 3: Add offline map regions and tiles
    this.version(3).stores({
      pendingMutations: '++id, createdAt, status',
      cachedTasks: 'id, tenant_id, incident_id, assigned_to, status',
      cachedShelters: 'id, tenant_id, incident_id, status',
      cachedBoardPosts: 'id, tenant_id, incident_id',
      userContext: 'user_id',
      cachedDamageAssessments: 'id, offline_id, tenant_id, incident_id, status, is_synced, updated_at',
      offlinePhotos: 'id, assessment_id, is_synced, created_at',
      offlineMapRegions: 'id, status, created_at',
      offlineMapTiles: 'id, regionId, [z+x+y+basemapId]',
    });

    // Version 4: Add Regrid parcel fields to cached damage assessments
    this.version(4).stores({
      pendingMutations: '++id, createdAt, status',
      cachedTasks: 'id, tenant_id, incident_id, assigned_to, status',
      cachedShelters: 'id, tenant_id, incident_id, status',
      cachedBoardPosts: 'id, tenant_id, incident_id',
      userContext: 'user_id',
      cachedDamageAssessments: 'id, offline_id, tenant_id, incident_id, status, is_synced, updated_at',
      offlinePhotos: 'id, assessment_id, is_synced, created_at',
      offlineMapRegions: 'id, status, created_at',
      offlineMapTiles: 'id, regionId, [z+x+y+basemapId]',
    });

    // Version 5: Add PDA zone offline support + GPS breadcrumbs
    this.version(5).stores({
      pendingMutations: '++id, createdAt, status',
      cachedTasks: 'id, tenant_id, incident_id, assigned_to, status',
      cachedShelters: 'id, tenant_id, incident_id, status',
      cachedBoardPosts: 'id, tenant_id, incident_id',
      userContext: 'user_id',
      cachedDamageAssessments: 'id, offline_id, tenant_id, incident_id, status, is_synced, updated_at',
      offlinePhotos: 'id, assessment_id, is_synced, created_at',
      offlineMapRegions: 'id, status, created_at',
      offlineMapTiles: 'id, regionId, [z+x+y+basemapId]',
      cachedPdaZoneSets: 'id, tenant_id, incident_id',
      cachedPdaZones: 'id, tenant_id, zone_set_id, status, assigned_to',
      offlineBreadcrumbs: 'id, zone_id, is_synced, recorded_at',
    });

    // Version 6: Add COOP offline support
    this.version(6).stores({
      pendingMutations: '++id, createdAt, status',
      cachedTasks: 'id, tenant_id, incident_id, assigned_to, status',
      cachedShelters: 'id, tenant_id, incident_id, status',
      cachedBoardPosts: 'id, tenant_id, incident_id',
      userContext: 'user_id',
      cachedDamageAssessments: 'id, offline_id, tenant_id, incident_id, status, is_synced, updated_at',
      offlinePhotos: 'id, assessment_id, is_synced, created_at',
      offlineMapRegions: 'id, status, created_at',
      offlineMapTiles: 'id, regionId, [z+x+y+basemapId]',
      cachedPdaZoneSets: 'id, tenant_id, incident_id',
      cachedPdaZones: 'id, tenant_id, zone_set_id, status, assigned_to',
      offlineBreadcrumbs: 'id, zone_id, is_synced, recorded_at',
      cachedCoopPlans: 'id, tenant_id, status, is_synced',
      cachedEssentialFunctions: 'id, plan_id, priority_tier, is_synced',
      cachedSuccessionOrders: 'id, plan_id, is_synced',
      cachedCoopFacilities: 'id, plan_id, readiness_status, is_synced',
      offlineActivationLogs: 'id, activation_id, is_synced, created_at',
    });

    // Version 7: Add SUAS offline snapshot support
    this.version(7).stores({
      pendingMutations: '++id, createdAt, status',
      cachedTasks: 'id, tenant_id, incident_id, assigned_to, status',
      cachedShelters: 'id, tenant_id, incident_id, status',
      cachedBoardPosts: 'id, tenant_id, incident_id',
      userContext: 'user_id',
      cachedDamageAssessments: 'id, offline_id, tenant_id, incident_id, status, is_synced, updated_at',
      offlinePhotos: 'id, assessment_id, is_synced, created_at',
      offlineMapRegions: 'id, status, created_at',
      offlineMapTiles: 'id, regionId, [z+x+y+basemapId]',
      cachedPdaZoneSets: 'id, tenant_id, incident_id',
      cachedPdaZones: 'id, tenant_id, zone_set_id, status, assigned_to',
      offlineBreadcrumbs: 'id, zone_id, is_synced, recorded_at',
      cachedCoopPlans: 'id, tenant_id, status, is_synced',
      cachedEssentialFunctions: 'id, plan_id, priority_tier, is_synced',
      cachedSuccessionOrders: 'id, plan_id, is_synced',
      cachedCoopFacilities: 'id, plan_id, readiness_status, is_synced',
      offlineActivationLogs: 'id, activation_id, is_synced, created_at',
      offlineSnapshots: 'id, mission_id, captured_at, is_synced',
    });

    // Version 8: Add board post offline sync support (is_synced index, updated_at)
    this.version(8).stores({
      pendingMutations: '++id, createdAt, status',
      cachedTasks: 'id, tenant_id, incident_id, assigned_to, status',
      cachedShelters: 'id, tenant_id, incident_id, status',
      cachedBoardPosts: 'id, offline_id, tenant_id, incident_id, is_synced, updated_at',
      userContext: 'user_id',
      cachedDamageAssessments: 'id, offline_id, tenant_id, incident_id, status, is_synced, updated_at',
      offlinePhotos: 'id, assessment_id, is_synced, created_at',
      offlineMapRegions: 'id, status, created_at',
      offlineMapTiles: 'id, regionId, [z+x+y+basemapId]',
      cachedPdaZoneSets: 'id, tenant_id, incident_id',
      cachedPdaZones: 'id, tenant_id, zone_set_id, status, assigned_to',
      offlineBreadcrumbs: 'id, zone_id, is_synced, recorded_at',
      cachedCoopPlans: 'id, tenant_id, status, is_synced',
      cachedEssentialFunctions: 'id, plan_id, priority_tier, is_synced',
      cachedSuccessionOrders: 'id, plan_id, is_synced',
      cachedCoopFacilities: 'id, plan_id, readiness_status, is_synced',
      offlineActivationLogs: 'id, activation_id, is_synced, created_at',
      offlineSnapshots: 'id, mission_id, captured_at, is_synced',
    });

    // Version 9: Add ICS 214 unit log offline support
    this.version(9).stores({
      pendingMutations: '++id, createdAt, status',
      cachedTasks: 'id, tenant_id, incident_id, assigned_to, status',
      cachedShelters: 'id, tenant_id, incident_id, status',
      cachedBoardPosts: 'id, offline_id, tenant_id, incident_id, is_synced, updated_at',
      userContext: 'user_id',
      cachedDamageAssessments: 'id, offline_id, tenant_id, incident_id, status, is_synced, updated_at',
      offlinePhotos: 'id, assessment_id, is_synced, created_at',
      offlineMapRegions: 'id, status, created_at',
      offlineMapTiles: 'id, regionId, [z+x+y+basemapId]',
      cachedPdaZoneSets: 'id, tenant_id, incident_id',
      cachedPdaZones: 'id, tenant_id, zone_set_id, status, assigned_to',
      offlineBreadcrumbs: 'id, zone_id, is_synced, recorded_at',
      cachedCoopPlans: 'id, tenant_id, status, is_synced',
      cachedEssentialFunctions: 'id, plan_id, priority_tier, is_synced',
      cachedSuccessionOrders: 'id, plan_id, is_synced',
      cachedCoopFacilities: 'id, plan_id, readiness_status, is_synced',
      offlineActivationLogs: 'id, activation_id, is_synced, created_at',
      offlineSnapshots: 'id, mission_id, captured_at, is_synced',
      cachedUnitLogs: 'id, offline_id, tenant_id, incident_id, status, is_synced, updated_at',
      cachedActivityEntries: 'id, offline_id, unit_log_id, tenant_id, is_synced, updated_at',
    });

    // Version 10: Add supply request offline support
    this.version(10).stores({
      pendingMutations: '++id, createdAt, status',
      cachedTasks: 'id, tenant_id, incident_id, assigned_to, status',
      cachedShelters: 'id, tenant_id, incident_id, status',
      cachedBoardPosts: 'id, offline_id, tenant_id, incident_id, is_synced, updated_at',
      userContext: 'user_id',
      cachedDamageAssessments: 'id, offline_id, tenant_id, incident_id, status, is_synced, updated_at',
      offlinePhotos: 'id, assessment_id, is_synced, created_at',
      offlineMapRegions: 'id, status, created_at',
      offlineMapTiles: 'id, regionId, [z+x+y+basemapId]',
      cachedPdaZoneSets: 'id, tenant_id, incident_id',
      cachedPdaZones: 'id, tenant_id, zone_set_id, status, assigned_to',
      offlineBreadcrumbs: 'id, zone_id, is_synced, recorded_at',
      cachedCoopPlans: 'id, tenant_id, status, is_synced',
      cachedEssentialFunctions: 'id, plan_id, priority_tier, is_synced',
      cachedSuccessionOrders: 'id, plan_id, is_synced',
      cachedCoopFacilities: 'id, plan_id, readiness_status, is_synced',
      offlineActivationLogs: 'id, activation_id, is_synced, created_at',
      offlineSnapshots: 'id, mission_id, captured_at, is_synced',
      cachedUnitLogs: 'id, offline_id, tenant_id, incident_id, status, is_synced, updated_at',
      cachedActivityEntries: 'id, offline_id, unit_log_id, tenant_id, is_synced, updated_at',
      cachedSupplyRequests: '&id, incident_id, status, priority, is_synced, updated_at',
    });

    // Version 11: Personnel GPS tracking position buffer for offline support
    this.version(11).stores({
      pendingMutations: '++id, createdAt, status',
      cachedTasks: 'id, tenant_id, incident_id, assigned_to, status',
      cachedShelters: 'id, tenant_id, incident_id, status',
      cachedBoardPosts: 'id, offline_id, tenant_id, incident_id, is_synced, updated_at',
      userContext: 'user_id',
      cachedDamageAssessments: 'id, offline_id, tenant_id, incident_id, status, is_synced, updated_at',
      offlinePhotos: 'id, assessment_id, is_synced, created_at',
      offlineMapRegions: 'id, status, created_at',
      offlineMapTiles: 'id, regionId, [z+x+y+basemapId]',
      cachedPdaZoneSets: 'id, tenant_id, incident_id',
      cachedPdaZones: 'id, tenant_id, zone_set_id, status, assigned_to',
      offlineBreadcrumbs: 'id, zone_id, is_synced, recorded_at',
      cachedCoopPlans: 'id, tenant_id, status, is_synced',
      cachedEssentialFunctions: 'id, plan_id, priority_tier, is_synced',
      cachedSuccessionOrders: 'id, plan_id, is_synced',
      cachedCoopFacilities: 'id, plan_id, readiness_status, is_synced',
      offlineActivationLogs: 'id, activation_id, is_synced, created_at',
      offlineSnapshots: 'id, mission_id, captured_at, is_synced',
      cachedUnitLogs: 'id, offline_id, tenant_id, incident_id, status, is_synced, updated_at',
      cachedActivityEntries: 'id, offline_id, unit_log_id, tenant_id, is_synced, updated_at',
      cachedSupplyRequests: '&id, incident_id, status, priority, is_synced, updated_at',
      personnelPositionBuffer: 'id, session_id, is_synced, recorded_at, created_at',
    });

    // Version 12: Add offline task sync tracking fields
    this.version(12).stores({
      pendingMutations: '++id, createdAt, status',
      cachedTasks: 'id, offline_id, tenant_id, incident_id, is_synced',
      cachedShelters: 'id, tenant_id, incident_id, status',
      cachedBoardPosts: 'id, offline_id, tenant_id, incident_id, is_synced, updated_at',
      userContext: 'user_id',
      cachedDamageAssessments: 'id, offline_id, tenant_id, incident_id, status, is_synced, updated_at',
      offlinePhotos: 'id, assessment_id, is_synced, created_at',
      offlineMapRegions: 'id, status, created_at',
      offlineMapTiles: 'id, regionId, [z+x+y+basemapId]',
      cachedPdaZoneSets: 'id, tenant_id, incident_id',
      cachedPdaZones: 'id, tenant_id, zone_set_id, status, assigned_to',
      offlineBreadcrumbs: 'id, zone_id, is_synced, recorded_at',
      cachedCoopPlans: 'id, tenant_id, status, is_synced',
      cachedEssentialFunctions: 'id, plan_id, priority_tier, is_synced',
      cachedSuccessionOrders: 'id, plan_id, is_synced',
      cachedCoopFacilities: 'id, plan_id, readiness_status, is_synced',
      offlineActivationLogs: 'id, activation_id, is_synced, created_at',
      offlineSnapshots: 'id, mission_id, captured_at, is_synced',
      cachedUnitLogs: 'id, offline_id, tenant_id, incident_id, status, is_synced, updated_at',
      cachedActivityEntries: 'id, offline_id, unit_log_id, tenant_id, is_synced, updated_at',
      cachedSupplyRequests: '&id, incident_id, status, priority, is_synced, updated_at',
      personnelPositionBuffer: 'id, session_id, is_synced, recorded_at, created_at',
    });

    // Version 13: Add infrastructure offline caching (critical infrastructure, road closures, utility outages)
    this.version(13).stores({
      pendingMutations: '++id, createdAt, status',
      cachedTasks: 'id, offline_id, tenant_id, incident_id, is_synced',
      cachedShelters: 'id, tenant_id, incident_id, status',
      cachedBoardPosts: 'id, offline_id, tenant_id, incident_id, is_synced, updated_at',
      userContext: 'user_id',
      cachedDamageAssessments: 'id, offline_id, tenant_id, incident_id, status, is_synced, updated_at',
      offlinePhotos: 'id, assessment_id, is_synced, created_at',
      offlineMapRegions: 'id, status, created_at',
      offlineMapTiles: 'id, regionId, [z+x+y+basemapId]',
      cachedPdaZoneSets: 'id, tenant_id, incident_id',
      cachedPdaZones: 'id, tenant_id, zone_set_id, status, assigned_to',
      offlineBreadcrumbs: 'id, zone_id, is_synced, recorded_at',
      cachedCoopPlans: 'id, tenant_id, status, is_synced',
      cachedEssentialFunctions: 'id, plan_id, priority_tier, is_synced',
      cachedSuccessionOrders: 'id, plan_id, is_synced',
      cachedCoopFacilities: 'id, plan_id, readiness_status, is_synced',
      offlineActivationLogs: 'id, activation_id, is_synced, created_at',
      offlineSnapshots: 'id, mission_id, captured_at, is_synced',
      cachedUnitLogs: 'id, offline_id, tenant_id, incident_id, status, is_synced, updated_at',
      cachedActivityEntries: 'id, offline_id, unit_log_id, tenant_id, is_synced, updated_at',
      cachedSupplyRequests: '&id, incident_id, status, priority, is_synced, updated_at',
      personnelPositionBuffer: 'id, session_id, is_synced, recorded_at, created_at',
      cachedCriticalInfrastructure: 'id, offline_id, tenant_id, incident_id, category, status, is_synced, updated_at',
      cachedRoadClosures: 'id, offline_id, tenant_id, incident_id, status, is_synced, updated_at',
      cachedUtilityOutages: 'id, offline_id, tenant_id, incident_id, utility_type, status, is_synced, updated_at',
    });

    // Version 14: Add conflict_state index to damage assessments for schema alignment (HIGH-2)
    this.version(14).stores({
      pendingMutations: '++id, createdAt, status',
      cachedTasks: 'id, offline_id, tenant_id, incident_id, is_synced',
      cachedShelters: 'id, tenant_id, incident_id, status',
      cachedBoardPosts: 'id, offline_id, tenant_id, incident_id, is_synced, updated_at',
      userContext: 'user_id',
      cachedDamageAssessments: 'id, offline_id, tenant_id, incident_id, status, is_synced, updated_at, conflict_state',
      offlinePhotos: 'id, assessment_id, is_synced, created_at',
      offlineMapRegions: 'id, status, created_at',
      offlineMapTiles: 'id, regionId, [z+x+y+basemapId]',
      cachedPdaZoneSets: 'id, tenant_id, incident_id',
      cachedPdaZones: 'id, tenant_id, zone_set_id, status, assigned_to',
      offlineBreadcrumbs: 'id, zone_id, is_synced, recorded_at',
      cachedCoopPlans: 'id, tenant_id, status, is_synced',
      cachedEssentialFunctions: 'id, plan_id, priority_tier, is_synced',
      cachedSuccessionOrders: 'id, plan_id, is_synced',
      cachedCoopFacilities: 'id, plan_id, readiness_status, is_synced',
      offlineActivationLogs: 'id, activation_id, is_synced, created_at',
      offlineSnapshots: 'id, mission_id, captured_at, is_synced',
      cachedUnitLogs: 'id, offline_id, tenant_id, incident_id, status, is_synced, updated_at',
      cachedActivityEntries: 'id, offline_id, unit_log_id, tenant_id, is_synced, updated_at',
      cachedSupplyRequests: '&id, incident_id, status, priority, is_synced, updated_at',
      personnelPositionBuffer: 'id, session_id, is_synced, recorded_at, created_at',
      cachedCriticalInfrastructure: 'id, offline_id, tenant_id, incident_id, category, status, is_synced, updated_at',
      cachedRoadClosures: 'id, offline_id, tenant_id, incident_id, status, is_synced, updated_at',
      cachedUtilityOutages: 'id, offline_id, tenant_id, incident_id, utility_type, status, is_synced, updated_at',
    });

    // Version 15: Add GPS track offline support
    this.version(15).stores({
      pendingMutations: '++id, createdAt, status',
      cachedTasks: 'id, offline_id, tenant_id, incident_id, is_synced',
      cachedShelters: 'id, tenant_id, incident_id, status',
      cachedBoardPosts: 'id, offline_id, tenant_id, incident_id, is_synced, updated_at',
      userContext: 'user_id',
      cachedDamageAssessments: 'id, offline_id, tenant_id, incident_id, status, is_synced, updated_at, conflict_state',
      offlinePhotos: 'id, assessment_id, is_synced, created_at',
      offlineMapRegions: 'id, status, created_at',
      offlineMapTiles: 'id, regionId, [z+x+y+basemapId]',
      cachedPdaZoneSets: 'id, tenant_id, incident_id',
      cachedPdaZones: 'id, tenant_id, zone_set_id, status, assigned_to',
      offlineBreadcrumbs: 'id, zone_id, is_synced, recorded_at',
      cachedCoopPlans: 'id, tenant_id, status, is_synced',
      cachedEssentialFunctions: 'id, plan_id, priority_tier, is_synced',
      cachedSuccessionOrders: 'id, plan_id, is_synced',
      cachedCoopFacilities: 'id, plan_id, readiness_status, is_synced',
      offlineActivationLogs: 'id, activation_id, is_synced, created_at',
      offlineSnapshots: 'id, mission_id, captured_at, is_synced',
      cachedUnitLogs: 'id, offline_id, tenant_id, incident_id, status, is_synced, updated_at',
      cachedActivityEntries: 'id, offline_id, unit_log_id, tenant_id, is_synced, updated_at',
      cachedSupplyRequests: '&id, incident_id, status, priority, is_synced, updated_at',
      personnelPositionBuffer: 'id, session_id, is_synced, recorded_at, created_at',
      cachedCriticalInfrastructure: 'id, offline_id, tenant_id, incident_id, category, status, is_synced, updated_at',
      cachedRoadClosures: 'id, offline_id, tenant_id, incident_id, status, is_synced, updated_at',
      cachedUtilityOutages: 'id, offline_id, tenant_id, incident_id, utility_type, status, is_synced, updated_at',
      offlineGpsTracks: '++id, trackId, tenantId, isSynced',
      offlineGpsTrackPoints: '++id, trackId, batchId, isSynced',
    });

    // Version 16: Add offline map feature cache
    this.version(16).stores({
      pendingMutations: '++id, createdAt, status',
      cachedTasks: 'id, offline_id, tenant_id, incident_id, is_synced',
      cachedShelters: 'id, tenant_id, incident_id, status',
      cachedBoardPosts: 'id, offline_id, tenant_id, incident_id, is_synced, updated_at',
      userContext: 'user_id',
      cachedDamageAssessments: 'id, offline_id, tenant_id, incident_id, status, is_synced, updated_at, conflict_state',
      offlinePhotos: 'id, assessment_id, is_synced, created_at',
      offlineMapRegions: 'id, status, created_at',
      offlineMapTiles: 'id, regionId, [z+x+y+basemapId]',
      cachedPdaZoneSets: 'id, tenant_id, incident_id',
      cachedPdaZones: 'id, tenant_id, zone_set_id, status, assigned_to',
      offlineBreadcrumbs: 'id, zone_id, is_synced, recorded_at',
      cachedCoopPlans: 'id, tenant_id, status, is_synced',
      cachedEssentialFunctions: 'id, plan_id, priority_tier, is_synced',
      cachedSuccessionOrders: 'id, plan_id, is_synced',
      cachedCoopFacilities: 'id, plan_id, readiness_status, is_synced',
      offlineActivationLogs: 'id, activation_id, is_synced, created_at',
      offlineSnapshots: 'id, mission_id, captured_at, is_synced',
      cachedUnitLogs: 'id, offline_id, tenant_id, incident_id, status, is_synced, updated_at',
      cachedActivityEntries: 'id, offline_id, unit_log_id, tenant_id, is_synced, updated_at',
      cachedSupplyRequests: '&id, incident_id, status, priority, is_synced, updated_at',
      personnelPositionBuffer: 'id, session_id, is_synced, recorded_at, created_at',
      cachedCriticalInfrastructure: 'id, offline_id, tenant_id, incident_id, category, status, is_synced, updated_at',
      cachedRoadClosures: 'id, offline_id, tenant_id, incident_id, status, is_synced, updated_at',
      cachedUtilityOutages: 'id, offline_id, tenant_id, incident_id, utility_type, status, is_synced, updated_at',
      offlineGpsTracks: '++id, trackId, tenantId, isSynced',
      offlineGpsTrackPoints: '++id, trackId, batchId, isSynced',
      cachedMapFeatures: 'id, tenant_id, layer, feature_id, is_synced, cached_at',
    });

    // Version 17: Add offline contact cache for field operator phone/email lookup
    this.version(17).stores({
      pendingMutations: '++id, createdAt, status',
      cachedTasks: 'id, offline_id, tenant_id, incident_id, is_synced',
      cachedShelters: 'id, tenant_id, incident_id, status',
      cachedBoardPosts: 'id, offline_id, tenant_id, incident_id, is_synced, updated_at',
      userContext: 'user_id',
      cachedDamageAssessments: 'id, offline_id, tenant_id, incident_id, status, is_synced, updated_at, conflict_state',
      offlinePhotos: 'id, assessment_id, is_synced, created_at',
      offlineMapRegions: 'id, status, created_at',
      offlineMapTiles: 'id, regionId, [z+x+y+basemapId]',
      cachedPdaZoneSets: 'id, tenant_id, incident_id',
      cachedPdaZones: 'id, tenant_id, zone_set_id, status, assigned_to',
      offlineBreadcrumbs: 'id, zone_id, is_synced, recorded_at',
      cachedCoopPlans: 'id, tenant_id, status, is_synced',
      cachedEssentialFunctions: 'id, plan_id, priority_tier, is_synced',
      cachedSuccessionOrders: 'id, plan_id, is_synced',
      cachedCoopFacilities: 'id, plan_id, readiness_status, is_synced',
      offlineActivationLogs: 'id, activation_id, is_synced, created_at',
      offlineSnapshots: 'id, mission_id, captured_at, is_synced',
      cachedUnitLogs: 'id, offline_id, tenant_id, incident_id, status, is_synced, updated_at',
      cachedActivityEntries: 'id, offline_id, unit_log_id, tenant_id, is_synced, updated_at',
      cachedSupplyRequests: '&id, incident_id, status, priority, is_synced, updated_at',
      personnelPositionBuffer: 'id, session_id, is_synced, recorded_at, created_at',
      cachedCriticalInfrastructure: 'id, offline_id, tenant_id, incident_id, category, status, is_synced, updated_at',
      cachedRoadClosures: 'id, offline_id, tenant_id, incident_id, status, is_synced, updated_at',
      cachedUtilityOutages: 'id, offline_id, tenant_id, incident_id, utility_type, status, is_synced, updated_at',
      offlineGpsTracks: '++id, trackId, tenantId, isSynced',
      offlineGpsTrackPoints: '++id, trackId, batchId, isSynced',
      cachedMapFeatures: 'id, tenant_id, layer, feature_id, is_synced, cached_at',
      cachedContacts: 'id, tenant_id, name, contact_type, is_active, is_synced, updated_at',
    });

    // Version 18: Add credential offline caching
    this.version(18).stores({
      pendingMutations: '++id, createdAt, status',
      cachedTasks: 'id, offline_id, tenant_id, incident_id, is_synced',
      cachedShelters: 'id, tenant_id, incident_id, status',
      cachedBoardPosts: 'id, offline_id, tenant_id, incident_id, is_synced, updated_at',
      userContext: 'user_id',
      cachedDamageAssessments: 'id, offline_id, tenant_id, incident_id, status, is_synced, updated_at, conflict_state',
      offlinePhotos: 'id, assessment_id, is_synced, created_at',
      offlineMapRegions: 'id, status, created_at',
      offlineMapTiles: 'id, regionId, [z+x+y+basemapId]',
      cachedPdaZoneSets: 'id, tenant_id, incident_id',
      cachedPdaZones: 'id, tenant_id, zone_set_id, status, assigned_to',
      offlineBreadcrumbs: 'id, zone_id, is_synced, recorded_at',
      cachedCoopPlans: 'id, tenant_id, status, is_synced',
      cachedEssentialFunctions: 'id, plan_id, priority_tier, is_synced',
      cachedSuccessionOrders: 'id, plan_id, is_synced',
      cachedCoopFacilities: 'id, plan_id, readiness_status, is_synced',
      offlineActivationLogs: 'id, activation_id, is_synced, created_at',
      offlineSnapshots: 'id, mission_id, captured_at, is_synced',
      cachedUnitLogs: 'id, offline_id, tenant_id, incident_id, status, is_synced, updated_at',
      cachedActivityEntries: 'id, offline_id, unit_log_id, tenant_id, is_synced, updated_at',
      cachedSupplyRequests: '&id, incident_id, status, priority, is_synced, updated_at',
      personnelPositionBuffer: 'id, session_id, is_synced, recorded_at, created_at',
      cachedCriticalInfrastructure: 'id, offline_id, tenant_id, incident_id, category, status, is_synced, updated_at',
      cachedRoadClosures: 'id, offline_id, tenant_id, incident_id, status, is_synced, updated_at',
      cachedUtilityOutages: 'id, offline_id, tenant_id, incident_id, utility_type, status, is_synced, updated_at',
      offlineGpsTracks: '++id, trackId, tenantId, isSynced',
      offlineGpsTrackPoints: '++id, trackId, batchId, isSynced',
      cachedMapFeatures: 'id, tenant_id, layer, feature_id, is_synced, cached_at',
      cachedContacts: 'id, tenant_id, name, contact_type, is_active, is_synced, updated_at',
      cachedCredentials: 'id, tenant_id, status, display_name, credential_number, is_synced, last_synced_at',
      cachedQualificationTypes: 'id, code, name, category',
    });

    // Version 19: Remove offlineGpsTracks and offlineGpsTrackPoints (replaced by personnelPositionBuffer)
    this.version(19).stores({
      pendingMutations: '++id, createdAt, status',
      cachedTasks: 'id, offline_id, tenant_id, incident_id, is_synced',
      cachedShelters: 'id, tenant_id, incident_id, status',
      cachedBoardPosts: 'id, offline_id, tenant_id, incident_id, is_synced, updated_at',
      userContext: 'user_id',
      cachedDamageAssessments: 'id, offline_id, tenant_id, incident_id, status, is_synced, updated_at, conflict_state',
      offlinePhotos: 'id, assessment_id, is_synced, created_at',
      offlineMapRegions: 'id, status, created_at',
      offlineMapTiles: 'id, regionId, [z+x+y+basemapId]',
      cachedPdaZoneSets: 'id, tenant_id, incident_id',
      cachedPdaZones: 'id, tenant_id, zone_set_id, status, assigned_to',
      offlineBreadcrumbs: 'id, zone_id, is_synced, recorded_at',
      cachedCoopPlans: 'id, tenant_id, status, is_synced',
      cachedEssentialFunctions: 'id, plan_id, priority_tier, is_synced',
      cachedSuccessionOrders: 'id, plan_id, is_synced',
      cachedCoopFacilities: 'id, plan_id, readiness_status, is_synced',
      offlineActivationLogs: 'id, activation_id, is_synced, created_at',
      offlineSnapshots: 'id, mission_id, captured_at, is_synced',
      cachedUnitLogs: 'id, offline_id, tenant_id, incident_id, status, is_synced, updated_at',
      cachedActivityEntries: 'id, offline_id, unit_log_id, tenant_id, is_synced, updated_at',
      cachedSupplyRequests: '&id, incident_id, status, priority, is_synced, updated_at',
      personnelPositionBuffer: 'id, session_id, is_synced, recorded_at, created_at',
      cachedCriticalInfrastructure: 'id, offline_id, tenant_id, incident_id, category, status, is_synced, updated_at',
      cachedRoadClosures: 'id, offline_id, tenant_id, incident_id, status, is_synced, updated_at',
      cachedUtilityOutages: 'id, offline_id, tenant_id, incident_id, utility_type, status, is_synced, updated_at',
      offlineGpsTracks: null,
      offlineGpsTrackPoints: null,
      cachedMapFeatures: 'id, tenant_id, layer, feature_id, is_synced, cached_at',
      cachedContacts: 'id, tenant_id, name, contact_type, is_active, is_synced, updated_at',
      cachedCredentials: 'id, tenant_id, status, display_name, credential_number, is_synced, last_synced_at',
      cachedQualificationTypes: 'id, code, name, category',
    });

    // Version 20: Add FEMA cost entry offline support
    this.version(20).stores({
      pendingMutations: '++id, createdAt, status',
      cachedTasks: 'id, offline_id, tenant_id, incident_id, is_synced',
      cachedShelters: 'id, tenant_id, incident_id, status',
      cachedBoardPosts: 'id, offline_id, tenant_id, incident_id, is_synced, updated_at',
      userContext: 'user_id',
      cachedDamageAssessments: 'id, offline_id, tenant_id, incident_id, status, is_synced, updated_at, conflict_state',
      offlinePhotos: 'id, assessment_id, is_synced, created_at',
      offlineMapRegions: 'id, status, created_at',
      offlineMapTiles: 'id, regionId, [z+x+y+basemapId]',
      cachedPdaZoneSets: 'id, tenant_id, incident_id',
      cachedPdaZones: 'id, tenant_id, zone_set_id, status, assigned_to',
      offlineBreadcrumbs: 'id, zone_id, is_synced, recorded_at',
      cachedCoopPlans: 'id, tenant_id, status, is_synced',
      cachedEssentialFunctions: 'id, plan_id, priority_tier, is_synced',
      cachedSuccessionOrders: 'id, plan_id, is_synced',
      cachedCoopFacilities: 'id, plan_id, readiness_status, is_synced',
      offlineActivationLogs: 'id, activation_id, is_synced, created_at',
      offlineSnapshots: 'id, mission_id, captured_at, is_synced',
      cachedUnitLogs: 'id, offline_id, tenant_id, incident_id, status, is_synced, updated_at',
      cachedActivityEntries: 'id, offline_id, unit_log_id, tenant_id, is_synced, updated_at',
      cachedSupplyRequests: '&id, incident_id, status, priority, is_synced, updated_at',
      personnelPositionBuffer: 'id, session_id, is_synced, recorded_at, created_at',
      cachedCriticalInfrastructure: 'id, offline_id, tenant_id, incident_id, category, status, is_synced, updated_at',
      cachedRoadClosures: 'id, offline_id, tenant_id, incident_id, status, is_synced, updated_at',
      cachedUtilityOutages: 'id, offline_id, tenant_id, incident_id, utility_type, status, is_synced, updated_at',
      offlineGpsTracks: null,
      offlineGpsTrackPoints: null,
      cachedMapFeatures: 'id, tenant_id, layer, feature_id, is_synced, cached_at',
      cachedContacts: 'id, tenant_id, name, contact_type, is_active, is_synced, updated_at',
      cachedCredentials: 'id, tenant_id, status, display_name, credential_number, is_synced, last_synced_at',
      cachedQualificationTypes: 'id, code, name, category',
      cachedFemaCosts: 'id, offline_id, tenant_id, incident_id, cost_type, pa_category, is_synced, updated_at',
    });

    // Version 21: Add cached photos (blobs) and cached members for offline photo sync
    this.version(21).stores({
      pendingMutations: '++id, createdAt, status',
      cachedTasks: 'id, offline_id, tenant_id, incident_id, is_synced',
      cachedShelters: 'id, tenant_id, incident_id, status',
      cachedBoardPosts: 'id, offline_id, tenant_id, incident_id, is_synced, updated_at',
      userContext: 'user_id',
      cachedDamageAssessments: 'id, offline_id, tenant_id, incident_id, status, is_synced, updated_at, conflict_state',
      offlinePhotos: 'id, assessment_id, is_synced, created_at',
      offlineMapRegions: 'id, status, created_at',
      offlineMapTiles: 'id, regionId, [z+x+y+basemapId]',
      cachedPdaZoneSets: 'id, tenant_id, incident_id',
      cachedPdaZones: 'id, tenant_id, zone_set_id, status, assigned_to',
      offlineBreadcrumbs: 'id, zone_id, is_synced, recorded_at',
      cachedCoopPlans: 'id, tenant_id, status, is_synced',
      cachedEssentialFunctions: 'id, plan_id, priority_tier, is_synced',
      cachedSuccessionOrders: 'id, plan_id, is_synced',
      cachedCoopFacilities: 'id, plan_id, readiness_status, is_synced',
      offlineActivationLogs: 'id, activation_id, is_synced, created_at',
      offlineSnapshots: 'id, mission_id, captured_at, is_synced',
      cachedUnitLogs: 'id, offline_id, tenant_id, incident_id, status, is_synced, updated_at',
      cachedActivityEntries: 'id, offline_id, unit_log_id, tenant_id, is_synced, updated_at',
      cachedSupplyRequests: '&id, incident_id, status, priority, is_synced, updated_at',
      personnelPositionBuffer: 'id, session_id, is_synced, recorded_at, created_at',
      cachedCriticalInfrastructure: 'id, offline_id, tenant_id, incident_id, category, status, is_synced, updated_at',
      cachedRoadClosures: 'id, offline_id, tenant_id, incident_id, status, is_synced, updated_at',
      cachedUtilityOutages: 'id, offline_id, tenant_id, incident_id, utility_type, status, is_synced, updated_at',
      offlineGpsTracks: null,
      offlineGpsTrackPoints: null,
      cachedMapFeatures: 'id, tenant_id, layer, feature_id, is_synced, cached_at',
      cachedContacts: 'id, tenant_id, name, contact_type, is_active, is_synced, updated_at',
      cachedCredentials: 'id, tenant_id, status, display_name, credential_number, is_synced, last_synced_at',
      cachedQualificationTypes: 'id, code, name, category',
      cachedFemaCosts: 'id, offline_id, tenant_id, incident_id, cost_type, pa_category, is_synced, updated_at',
      cachedPhotos: 'id, entity_type, entity_id, cached_at',
      cachedMembers: 'id, tenant_id, name, email, is_synced',
    });

    // Version 22: Add offline orthophoto tile caching
    this.version(22).stores({
      pendingMutations: '++id, createdAt, status',
      cachedTasks: 'id, offline_id, tenant_id, incident_id, is_synced',
      cachedShelters: 'id, tenant_id, incident_id, status',
      cachedBoardPosts: 'id, offline_id, tenant_id, incident_id, is_synced, updated_at',
      userContext: 'user_id',
      cachedDamageAssessments: 'id, offline_id, tenant_id, incident_id, status, is_synced, updated_at, conflict_state',
      offlinePhotos: 'id, assessment_id, is_synced, created_at',
      offlineMapRegions: 'id, status, created_at',
      offlineMapTiles: 'id, regionId, [z+x+y+basemapId]',
      cachedPdaZoneSets: 'id, tenant_id, incident_id',
      cachedPdaZones: 'id, tenant_id, zone_set_id, status, assigned_to',
      offlineBreadcrumbs: 'id, zone_id, is_synced, recorded_at',
      cachedCoopPlans: 'id, tenant_id, status, is_synced',
      cachedEssentialFunctions: 'id, plan_id, priority_tier, is_synced',
      cachedSuccessionOrders: 'id, plan_id, is_synced',
      cachedCoopFacilities: 'id, plan_id, readiness_status, is_synced',
      offlineActivationLogs: 'id, activation_id, is_synced, created_at',
      offlineSnapshots: 'id, mission_id, captured_at, is_synced',
      cachedUnitLogs: 'id, offline_id, tenant_id, incident_id, status, is_synced, updated_at',
      cachedActivityEntries: 'id, offline_id, unit_log_id, tenant_id, is_synced, updated_at',
      cachedSupplyRequests: '&id, incident_id, status, priority, is_synced, updated_at',
      personnelPositionBuffer: 'id, session_id, is_synced, recorded_at, created_at',
      cachedCriticalInfrastructure: 'id, offline_id, tenant_id, incident_id, category, status, is_synced, updated_at',
      cachedRoadClosures: 'id, offline_id, tenant_id, incident_id, status, is_synced, updated_at',
      cachedUtilityOutages: 'id, offline_id, tenant_id, incident_id, utility_type, status, is_synced, updated_at',
      offlineGpsTracks: null,
      offlineGpsTrackPoints: null,
      cachedMapFeatures: 'id, tenant_id, layer, feature_id, is_synced, cached_at',
      cachedContacts: 'id, tenant_id, name, contact_type, is_active, is_synced, updated_at',
      cachedCredentials: 'id, tenant_id, status, display_name, credential_number, is_synced, last_synced_at',
      cachedQualificationTypes: 'id, code, name, category',
      cachedFemaCosts: 'id, offline_id, tenant_id, incident_id, cost_type, pa_category, is_synced, updated_at',
      cachedPhotos: 'id, entity_type, entity_id, cached_at',
      cachedMembers: 'id, tenant_id, name, email, is_synced',
      cachedOrthophotoRegions: 'id, task_id, name, created_at',
      cachedOrthophotoTiles: '++id, region_id, task_id, z, x, y, [task_id+z+x+y]',
    });

    // Version 23: Add ICS 214 roster offline support (pending mutations + cached entries)
    this.version(23).stores({
      pendingMutations: '++id, createdAt, status',
      cachedTasks: 'id, offline_id, tenant_id, incident_id, is_synced',
      cachedShelters: 'id, tenant_id, incident_id, status',
      cachedBoardPosts: 'id, offline_id, tenant_id, incident_id, is_synced, updated_at',
      userContext: 'user_id',
      cachedDamageAssessments: 'id, offline_id, tenant_id, incident_id, status, is_synced, updated_at, conflict_state',
      offlinePhotos: 'id, assessment_id, is_synced, created_at',
      offlineMapRegions: 'id, status, created_at',
      offlineMapTiles: 'id, regionId, [z+x+y+basemapId]',
      cachedPdaZoneSets: 'id, tenant_id, incident_id',
      cachedPdaZones: 'id, tenant_id, zone_set_id, status, assigned_to',
      offlineBreadcrumbs: 'id, zone_id, is_synced, recorded_at',
      cachedCoopPlans: 'id, tenant_id, status, is_synced',
      cachedEssentialFunctions: 'id, plan_id, priority_tier, is_synced',
      cachedSuccessionOrders: 'id, plan_id, is_synced',
      cachedCoopFacilities: 'id, plan_id, readiness_status, is_synced',
      offlineActivationLogs: 'id, activation_id, is_synced, created_at',
      offlineSnapshots: 'id, mission_id, captured_at, is_synced',
      cachedUnitLogs: 'id, offline_id, tenant_id, incident_id, status, is_synced, updated_at',
      cachedActivityEntries: 'id, offline_id, unit_log_id, tenant_id, is_synced, updated_at',
      cachedSupplyRequests: '&id, incident_id, status, priority, is_synced, updated_at',
      personnelPositionBuffer: 'id, session_id, is_synced, recorded_at, created_at',
      cachedCriticalInfrastructure: 'id, offline_id, tenant_id, incident_id, category, status, is_synced, updated_at',
      cachedRoadClosures: 'id, offline_id, tenant_id, incident_id, status, is_synced, updated_at',
      cachedUtilityOutages: 'id, offline_id, tenant_id, incident_id, utility_type, status, is_synced, updated_at',
      offlineGpsTracks: null,
      offlineGpsTrackPoints: null,
      cachedMapFeatures: 'id, tenant_id, layer, feature_id, is_synced, cached_at',
      cachedContacts: 'id, tenant_id, name, contact_type, is_active, is_synced, updated_at',
      cachedCredentials: 'id, tenant_id, status, display_name, credential_number, is_synced, last_synced_at',
      cachedQualificationTypes: 'id, code, name, category',
      cachedFemaCosts: 'id, offline_id, tenant_id, incident_id, cost_type, pa_category, is_synced, updated_at',
      cachedPhotos: 'id, entity_type, entity_id, cached_at',
      cachedMembers: 'id, tenant_id, name, email, is_synced',
      cachedOrthophotoRegions: 'id, task_id, name, created_at',
      cachedOrthophotoTiles: '++id, region_id, task_id, z, x, y, [task_id+z+x+y]',
      ics214RosterPending: '++id, unit_log_id, action, synced_at',
      cachedRosterEntries: 'id, unit_log_id, is_synced',
    });

    // Version 24: Add form drafts for auto-save (local-only, prevents work loss)
    this.version(24).stores({
      pendingMutations: '++id, createdAt, status',
      cachedTasks: 'id, offline_id, tenant_id, incident_id, is_synced',
      cachedShelters: 'id, tenant_id, incident_id, status',
      cachedBoardPosts: 'id, offline_id, tenant_id, incident_id, is_synced, updated_at',
      userContext: 'user_id',
      cachedDamageAssessments: 'id, offline_id, tenant_id, incident_id, status, is_synced, updated_at, conflict_state',
      offlinePhotos: 'id, assessment_id, is_synced, created_at',
      offlineMapRegions: 'id, status, created_at',
      offlineMapTiles: 'id, regionId, [z+x+y+basemapId]',
      cachedPdaZoneSets: 'id, tenant_id, incident_id',
      cachedPdaZones: 'id, tenant_id, zone_set_id, status, assigned_to',
      offlineBreadcrumbs: 'id, zone_id, is_synced, recorded_at',
      cachedCoopPlans: 'id, tenant_id, status, is_synced',
      cachedEssentialFunctions: 'id, plan_id, priority_tier, is_synced',
      cachedSuccessionOrders: 'id, plan_id, is_synced',
      cachedCoopFacilities: 'id, plan_id, readiness_status, is_synced',
      offlineActivationLogs: 'id, activation_id, is_synced, created_at',
      offlineSnapshots: 'id, mission_id, captured_at, is_synced',
      cachedUnitLogs: 'id, offline_id, tenant_id, incident_id, status, is_synced, updated_at',
      cachedActivityEntries: 'id, offline_id, unit_log_id, tenant_id, is_synced, updated_at',
      cachedSupplyRequests: '&id, incident_id, status, priority, is_synced, updated_at',
      personnelPositionBuffer: 'id, session_id, is_synced, recorded_at, created_at',
      cachedCriticalInfrastructure: 'id, offline_id, tenant_id, incident_id, category, status, is_synced, updated_at',
      cachedRoadClosures: 'id, offline_id, tenant_id, incident_id, status, is_synced, updated_at',
      cachedUtilityOutages: 'id, offline_id, tenant_id, incident_id, utility_type, status, is_synced, updated_at',
      offlineGpsTracks: null,
      offlineGpsTrackPoints: null,
      cachedMapFeatures: 'id, tenant_id, layer, feature_id, is_synced, cached_at',
      cachedContacts: 'id, tenant_id, name, contact_type, is_active, is_synced, updated_at',
      cachedCredentials: 'id, tenant_id, status, display_name, credential_number, is_synced, last_synced_at',
      cachedQualificationTypes: 'id, code, name, category',
      cachedFemaCosts: 'id, offline_id, tenant_id, incident_id, cost_type, pa_category, is_synced, updated_at',
      cachedPhotos: 'id, entity_type, entity_id, cached_at',
      cachedMembers: 'id, tenant_id, name, email, is_synced',
      cachedOrthophotoRegions: 'id, task_id, name, created_at',
      cachedOrthophotoTiles: '++id, region_id, task_id, z, x, y, [task_id+z+x+y]',
      ics214RosterPending: '++id, unit_log_id, action, synced_at',
      cachedRosterEntries: 'id, unit_log_id, is_synced',
      formDrafts: '++id, form_id, template_id, incident_id, updated_at, is_synced',
    });

    // Version 25: Add offline tasks table for full offline CRUD with auto-increment local IDs
    // NOTE: All tables from v24 must be carried forward to avoid Dexie dropping them on upgrade
    this.version(25).stores({
      pendingMutations: '++id, createdAt, status',
      cachedTasks: 'id, offline_id, tenant_id, incident_id, is_synced',
      cachedShelters: 'id, tenant_id, incident_id, status',
      cachedBoardPosts: 'id, offline_id, tenant_id, incident_id, is_synced, updated_at',
      userContext: 'user_id',
      cachedDamageAssessments: 'id, offline_id, tenant_id, incident_id, status, is_synced, updated_at, conflict_state',
      offlinePhotos: 'id, assessment_id, is_synced, created_at',
      offlineMapRegions: 'id, status, created_at',
      offlineMapTiles: 'id, regionId, [z+x+y+basemapId]',
      cachedPdaZoneSets: 'id, tenant_id, incident_id',
      cachedPdaZones: 'id, tenant_id, zone_set_id, status, assigned_to',
      offlineBreadcrumbs: 'id, zone_id, is_synced, recorded_at',
      cachedCoopPlans: 'id, tenant_id, status, is_synced',
      cachedEssentialFunctions: 'id, plan_id, priority_tier, is_synced',
      cachedSuccessionOrders: 'id, plan_id, is_synced',
      cachedCoopFacilities: 'id, plan_id, readiness_status, is_synced',
      offlineActivationLogs: 'id, activation_id, is_synced, created_at',
      offlineSnapshots: 'id, mission_id, captured_at, is_synced',
      cachedUnitLogs: 'id, offline_id, tenant_id, incident_id, status, is_synced, updated_at',
      cachedActivityEntries: 'id, offline_id, unit_log_id, tenant_id, is_synced, updated_at',
      cachedSupplyRequests: '&id, incident_id, status, priority, is_synced, updated_at',
      personnelPositionBuffer: 'id, session_id, is_synced, recorded_at, created_at',
      cachedCriticalInfrastructure: 'id, offline_id, tenant_id, incident_id, category, status, is_synced, updated_at',
      cachedRoadClosures: 'id, offline_id, tenant_id, incident_id, status, is_synced, updated_at',
      cachedUtilityOutages: 'id, offline_id, tenant_id, incident_id, utility_type, status, is_synced, updated_at',
      offlineGpsTracks: null,
      offlineGpsTrackPoints: null,
      cachedMapFeatures: 'id, tenant_id, layer, feature_id, is_synced, cached_at',
      cachedContacts: 'id, tenant_id, name, contact_type, is_active, is_synced, updated_at',
      cachedCredentials: 'id, tenant_id, status, display_name, credential_number, is_synced, last_synced_at',
      cachedQualificationTypes: 'id, code, name, category',
      cachedFemaCosts: 'id, offline_id, tenant_id, incident_id, cost_type, pa_category, is_synced, updated_at',
      cachedPhotos: 'id, entity_type, entity_id, cached_at',
      cachedMembers: 'id, tenant_id, name, email, is_synced',
      cachedOrthophotoRegions: 'id, task_id, name, created_at',
      cachedOrthophotoTiles: '++id, region_id, task_id, z, x, y, [task_id+z+x+y]',
      ics214RosterPending: '++id, unit_log_id, action, synced_at',
      cachedRosterEntries: 'id, unit_log_id, is_synced',
      formDrafts: '++id, form_id, template_id, incident_id, updated_at, is_synced',
      // New in v25:
      offlineTasks: '++localId, id, incident_id, tenant_id, is_synced, updated_at, assigned_to, status',
    });

    // Version 26: Add offline forms and cached form templates
    this.version(26).stores({
      pendingMutations: '++id, createdAt, status',
      cachedTasks: 'id, offline_id, tenant_id, incident_id, is_synced',
      cachedShelters: 'id, tenant_id, incident_id, status',
      cachedBoardPosts: 'id, offline_id, tenant_id, incident_id, is_synced, updated_at',
      userContext: 'user_id',
      cachedDamageAssessments: 'id, offline_id, tenant_id, incident_id, status, is_synced, updated_at, conflict_state',
      offlinePhotos: 'id, assessment_id, is_synced, created_at',
      offlineMapRegions: 'id, status, created_at',
      offlineMapTiles: 'id, regionId, [z+x+y+basemapId]',
      cachedPdaZoneSets: 'id, tenant_id, incident_id',
      cachedPdaZones: 'id, tenant_id, zone_set_id, status, assigned_to',
      offlineBreadcrumbs: 'id, zone_id, is_synced, recorded_at',
      cachedCoopPlans: 'id, tenant_id, status, is_synced',
      cachedEssentialFunctions: 'id, plan_id, priority_tier, is_synced',
      cachedSuccessionOrders: 'id, plan_id, is_synced',
      cachedCoopFacilities: 'id, plan_id, readiness_status, is_synced',
      offlineActivationLogs: 'id, activation_id, is_synced, created_at',
      offlineSnapshots: 'id, mission_id, captured_at, is_synced',
      cachedUnitLogs: 'id, offline_id, tenant_id, incident_id, status, is_synced, updated_at',
      cachedActivityEntries: 'id, offline_id, unit_log_id, tenant_id, is_synced, updated_at',
      cachedSupplyRequests: '&id, incident_id, status, priority, is_synced, updated_at',
      personnelPositionBuffer: 'id, session_id, is_synced, recorded_at, created_at',
      cachedCriticalInfrastructure: 'id, offline_id, tenant_id, incident_id, category, status, is_synced, updated_at',
      cachedRoadClosures: 'id, offline_id, tenant_id, incident_id, status, is_synced, updated_at',
      cachedUtilityOutages: 'id, offline_id, tenant_id, incident_id, utility_type, status, is_synced, updated_at',
      offlineGpsTracks: null,
      offlineGpsTrackPoints: null,
      cachedMapFeatures: 'id, tenant_id, layer, feature_id, is_synced, cached_at',
      cachedContacts: 'id, tenant_id, name, contact_type, is_active, is_synced, updated_at',
      cachedCredentials: 'id, tenant_id, status, display_name, credential_number, is_synced, last_synced_at',
      cachedQualificationTypes: 'id, code, name, category',
      cachedFemaCosts: 'id, offline_id, tenant_id, incident_id, cost_type, pa_category, is_synced, updated_at',
      cachedPhotos: 'id, entity_type, entity_id, cached_at',
      cachedMembers: 'id, tenant_id, name, email, is_synced',
      cachedOrthophotoRegions: 'id, task_id, name, created_at',
      cachedOrthophotoTiles: '++id, region_id, task_id, z, x, y, [task_id+z+x+y]',
      ics214RosterPending: '++id, unit_log_id, action, synced_at',
      cachedRosterEntries: 'id, unit_log_id, is_synced',
      formDrafts: '++id, form_id, template_id, incident_id, updated_at, is_synced',
      offlineTasks: '++localId, id, incident_id, tenant_id, is_synced, updated_at, assigned_to, status',
      // New in v26:
      offlineForms: '++localId, id, offline_id, template_id, incident_id, tenant_id, is_synced, status, updated_at',
      cachedFormTemplates: 'id, code, name, category, is_active',
    });

    // Version 27: Add offline shelter check-in support
    this.version(27).stores({
      pendingMutations: '++id, createdAt, status',
      cachedTasks: 'id, offline_id, tenant_id, incident_id, is_synced',
      cachedShelters: 'id, tenant_id, incident_id, status',
      cachedBoardPosts: 'id, offline_id, tenant_id, incident_id, is_synced, updated_at',
      userContext: 'user_id',
      cachedDamageAssessments: 'id, offline_id, tenant_id, incident_id, status, is_synced, updated_at, conflict_state',
      offlinePhotos: 'id, assessment_id, is_synced, created_at',
      offlineMapRegions: 'id, status, created_at',
      offlineMapTiles: 'id, regionId, [z+x+y+basemapId]',
      cachedPdaZoneSets: 'id, tenant_id, incident_id',
      cachedPdaZones: 'id, tenant_id, zone_set_id, status, assigned_to',
      offlineBreadcrumbs: 'id, zone_id, is_synced, recorded_at',
      cachedCoopPlans: 'id, tenant_id, status, is_synced',
      cachedEssentialFunctions: 'id, plan_id, priority_tier, is_synced',
      cachedSuccessionOrders: 'id, plan_id, is_synced',
      cachedCoopFacilities: 'id, plan_id, readiness_status, is_synced',
      offlineActivationLogs: 'id, activation_id, is_synced, created_at',
      offlineSnapshots: 'id, mission_id, captured_at, is_synced',
      cachedUnitLogs: 'id, offline_id, tenant_id, incident_id, status, is_synced, updated_at',
      cachedActivityEntries: 'id, offline_id, unit_log_id, tenant_id, is_synced, updated_at',
      cachedSupplyRequests: '&id, incident_id, status, priority, is_synced, updated_at',
      personnelPositionBuffer: 'id, session_id, is_synced, recorded_at, created_at',
      cachedCriticalInfrastructure: 'id, offline_id, tenant_id, incident_id, category, status, is_synced, updated_at',
      cachedRoadClosures: 'id, offline_id, tenant_id, incident_id, status, is_synced, updated_at',
      cachedUtilityOutages: 'id, offline_id, tenant_id, incident_id, utility_type, status, is_synced, updated_at',
      offlineGpsTracks: null,
      offlineGpsTrackPoints: null,
      cachedMapFeatures: 'id, tenant_id, layer, feature_id, is_synced, cached_at',
      cachedContacts: 'id, tenant_id, name, contact_type, is_active, is_synced, updated_at',
      cachedCredentials: 'id, tenant_id, status, display_name, credential_number, is_synced, last_synced_at',
      cachedQualificationTypes: 'id, code, name, category',
      cachedFemaCosts: 'id, offline_id, tenant_id, incident_id, cost_type, pa_category, is_synced, updated_at',
      cachedPhotos: 'id, entity_type, entity_id, cached_at',
      cachedMembers: 'id, tenant_id, name, email, is_synced',
      cachedOrthophotoRegions: 'id, task_id, name, created_at',
      cachedOrthophotoTiles: '++id, region_id, task_id, z, x, y, [task_id+z+x+y]',
      ics214RosterPending: '++id, unit_log_id, action, synced_at',
      cachedRosterEntries: 'id, unit_log_id, is_synced',
      formDrafts: '++id, form_id, template_id, incident_id, updated_at, is_synced',
      offlineTasks: '++localId, id, incident_id, tenant_id, is_synced, updated_at, assigned_to, status',
      offlineForms: '++localId, id, offline_id, template_id, incident_id, tenant_id, is_synced, status, updated_at',
      cachedFormTemplates: 'id, code, name, category, is_active',
      // New in v27:
      offlineCheckins: '++localId, id, shelter_id, tenant_id, is_synced, created_at',
    });

    // Version 28: Add offline supply request creation table and resource type cache
    this.version(28).stores({
      pendingMutations: '++id, createdAt, status',
      cachedTasks: 'id, offline_id, tenant_id, incident_id, is_synced',
      cachedShelters: 'id, tenant_id, incident_id, status',
      cachedBoardPosts: 'id, offline_id, tenant_id, incident_id, is_synced, updated_at',
      userContext: 'user_id',
      cachedDamageAssessments: 'id, offline_id, tenant_id, incident_id, status, is_synced, updated_at, conflict_state',
      offlinePhotos: 'id, assessment_id, is_synced, created_at',
      offlineMapRegions: 'id, status, created_at',
      offlineMapTiles: 'id, regionId, [z+x+y+basemapId]',
      cachedPdaZoneSets: 'id, tenant_id, incident_id',
      cachedPdaZones: 'id, tenant_id, zone_set_id, status, assigned_to',
      offlineBreadcrumbs: 'id, zone_id, is_synced, recorded_at',
      cachedCoopPlans: 'id, tenant_id, status, is_synced',
      cachedEssentialFunctions: 'id, plan_id, priority_tier, is_synced',
      cachedSuccessionOrders: 'id, plan_id, is_synced',
      cachedCoopFacilities: 'id, plan_id, readiness_status, is_synced',
      offlineActivationLogs: 'id, activation_id, is_synced, created_at',
      offlineSnapshots: 'id, mission_id, captured_at, is_synced',
      cachedUnitLogs: 'id, offline_id, tenant_id, incident_id, status, is_synced, updated_at',
      cachedActivityEntries: 'id, offline_id, unit_log_id, tenant_id, is_synced, updated_at',
      cachedSupplyRequests: '&id, incident_id, status, priority, is_synced, updated_at',
      personnelPositionBuffer: 'id, session_id, is_synced, recorded_at, created_at',
      cachedCriticalInfrastructure: 'id, offline_id, tenant_id, incident_id, category, status, is_synced, updated_at',
      cachedRoadClosures: 'id, offline_id, tenant_id, incident_id, status, is_synced, updated_at',
      cachedUtilityOutages: 'id, offline_id, tenant_id, incident_id, utility_type, status, is_synced, updated_at',
      offlineGpsTracks: null,
      offlineGpsTrackPoints: null,
      cachedMapFeatures: 'id, tenant_id, layer, feature_id, is_synced, cached_at',
      cachedContacts: 'id, tenant_id, name, contact_type, is_active, is_synced, updated_at',
      cachedCredentials: 'id, tenant_id, status, display_name, credential_number, is_synced, last_synced_at',
      cachedQualificationTypes: 'id, code, name, category',
      cachedFemaCosts: 'id, offline_id, tenant_id, incident_id, cost_type, pa_category, is_synced, updated_at',
      cachedPhotos: 'id, entity_type, entity_id, cached_at',
      cachedMembers: 'id, tenant_id, name, email, is_synced',
      cachedOrthophotoRegions: 'id, task_id, name, created_at',
      cachedOrthophotoTiles: '++id, region_id, task_id, z, x, y, [task_id+z+x+y]',
      ics214RosterPending: '++id, unit_log_id, action, synced_at',
      cachedRosterEntries: 'id, unit_log_id, is_synced',
      formDrafts: '++id, form_id, template_id, incident_id, updated_at, is_synced',
      offlineTasks: '++localId, id, incident_id, tenant_id, is_synced, updated_at, assigned_to, status',
      offlineForms: '++localId, id, offline_id, template_id, incident_id, tenant_id, is_synced, status, updated_at',
      cachedFormTemplates: 'id, code, name, category, is_active',
      offlineCheckins: '++localId, id, shelter_id, tenant_id, is_synced, created_at',
      // New in v28:
      offlineSupplyRequests: '++localId, id, incident_id, tenant_id, is_synced, status, created_at',
      cachedResourceTypes: 'id, category_id, code, name',
    });

    // Version 29: Add user_id to formDrafts index for efficient getDraftByTemplate lookups
    this.version(29).stores({
      pendingMutations: '++id, createdAt, status',
      cachedTasks: 'id, offline_id, tenant_id, incident_id, is_synced',
      cachedShelters: 'id, tenant_id, incident_id, status',
      cachedBoardPosts: 'id, offline_id, tenant_id, incident_id, is_synced, updated_at',
      userContext: 'user_id',
      cachedDamageAssessments: 'id, offline_id, tenant_id, incident_id, status, is_synced, updated_at, conflict_state',
      offlinePhotos: 'id, assessment_id, is_synced, created_at',
      offlineMapRegions: 'id, status, created_at',
      offlineMapTiles: 'id, regionId, [z+x+y+basemapId]',
      cachedPdaZoneSets: 'id, tenant_id, incident_id',
      cachedPdaZones: 'id, tenant_id, zone_set_id, status, assigned_to',
      offlineBreadcrumbs: 'id, zone_id, is_synced, recorded_at',
      cachedCoopPlans: 'id, tenant_id, status, is_synced',
      cachedEssentialFunctions: 'id, plan_id, priority_tier, is_synced',
      cachedSuccessionOrders: 'id, plan_id, is_synced',
      cachedCoopFacilities: 'id, plan_id, readiness_status, is_synced',
      offlineActivationLogs: 'id, activation_id, is_synced, created_at',
      offlineSnapshots: 'id, mission_id, captured_at, is_synced',
      cachedUnitLogs: 'id, offline_id, tenant_id, incident_id, status, is_synced, updated_at',
      cachedActivityEntries: 'id, offline_id, unit_log_id, tenant_id, is_synced, updated_at',
      cachedSupplyRequests: '&id, incident_id, status, priority, is_synced, updated_at',
      personnelPositionBuffer: 'id, session_id, is_synced, recorded_at, created_at',
      cachedCriticalInfrastructure: 'id, offline_id, tenant_id, incident_id, category, status, is_synced, updated_at',
      cachedRoadClosures: 'id, offline_id, tenant_id, incident_id, status, is_synced, updated_at',
      cachedUtilityOutages: 'id, offline_id, tenant_id, incident_id, utility_type, status, is_synced, updated_at',
      offlineGpsTracks: null,
      offlineGpsTrackPoints: null,
      cachedMapFeatures: 'id, tenant_id, layer, feature_id, is_synced, cached_at',
      cachedContacts: 'id, tenant_id, name, contact_type, is_active, is_synced, updated_at',
      cachedCredentials: 'id, tenant_id, status, display_name, credential_number, is_synced, last_synced_at',
      cachedQualificationTypes: 'id, code, name, category',
      cachedFemaCosts: 'id, offline_id, tenant_id, incident_id, cost_type, pa_category, is_synced, updated_at',
      cachedPhotos: 'id, entity_type, entity_id, cached_at',
      cachedMembers: 'id, tenant_id, name, email, is_synced',
      cachedOrthophotoRegions: 'id, task_id, name, created_at',
      cachedOrthophotoTiles: '++id, region_id, task_id, z, x, y, [task_id+z+x+y]',
      ics214RosterPending: '++id, unit_log_id, action, synced_at',
      cachedRosterEntries: 'id, unit_log_id, is_synced',
      formDrafts: '++id, form_id, template_id, incident_id, updated_at, is_synced, user_id, [template_id+user_id]',
      offlineTasks: '++localId, id, incident_id, tenant_id, is_synced, updated_at, assigned_to, status',
      offlineForms: '++localId, id, offline_id, template_id, incident_id, tenant_id, is_synced, status, updated_at',
      cachedFormTemplates: 'id, code, name, category, is_active',
      offlineCheckins: '++localId, id, shelter_id, tenant_id, is_synced, created_at',
      offlineSupplyRequests: '++localId, id, incident_id, tenant_id, is_synced, status, created_at',
      cachedResourceTypes: 'id, category_id, code, name',
    });

    // Version 30: Add cachedIncidents for offline incident creation.
    this.version(30).stores({
      cachedIncidents:
        'id, offline_id, tenant_id, status, is_synced, updated_at, conflict_state',
    });

    // Version 31: Add tenant_id to pendingMutations so offline mutations are
    // tenant-scoped and can never replay against the wrong tenant after a switch.
    // Only the changed table is declared; Dexie carries forward all others.
    this.version(31).stores({
      pendingMutations: '++id, createdAt, status, tenant_id',
    });

    // Version 32 (migration plan P0-20, finding C27): drop the offline basemap
    // tile scraper's tables. It bulk-downloaded Esri / OpenStreetMap tiles
    // (a terms risk) into offlineMapTiles and nothing ever read them back.
    // Deleting the object stores here frees whatever a device downloaded, on
    // the first open after this update. Server-built offline map packages
    // replace it later; they get their own tables, so do not revive these.
    this.version(32).stores({
      offlineMapTiles: null,
      offlineMapRegions: null,
    });

    // Version 33 (migration plan P2-05, finding C28): drop two tables no code
    // has ever written. `offlineTasks` was declared for a "full offline CRUD"
    // task store that never shipped (field tasks live in cachedTasks), and
    // `userContext` for an engaged-incident cache that the incident store
    // replaced. `git log -S` finds no write to either in the repository's
    // history, so no device holds rows in them; neither is in the drain list.
    this.version(33).stores({
      offlineTasks: null,
      userContext: null,
    });

    // Version 34 (migration plan S-06, §6.2 step 2(a)): the safety outbox. An
    // SOS is written here before its first send and kept until the field
    // endpoint stores it; the drain sends it first and hands every open one
    // (no human acknowledgement yet) to the MAUI app. Additive: a new table.
    this.version(34).stores({
      safetyOutbox: 'event_id, kind, status, created_at',
    });
  }
}

export const db = new OfflineDatabase();

// S-06: every write that the drain receipt depends on is tracked from the very
// first one (drain-write-tracker.ts), before any write path can run.
installDrainWriteHooks(db as unknown as Parameters<typeof installDrainWriteHooks>[0]);
